export const DEFAULT_METRIC = 'not_updated_stores';

export const METRIC_GROUPS = [
  {
    group: 'count',
    groupLabel: 'Count',
    options: [
      {
        value: 'total_stores',
        label: 'Total stores',
        description: 'Total number of stores carrying this product for this banner.',
        family: 'scope',
        countField: 'total_stores',
      },
      {
        value: 'not_updated_stores',
        label: 'Price not updated',
        description: 'Stores where last collected price is older than 14 days.',
        family: 'price_bad',
        countField: 'not_updated_stores',
      },
      {
        value: 'updated_stores',
        label: 'Price updated',
        description: 'Stores where last collected price is within the last 14 days.',
        family: 'price_good',
        countField: 'updated_stores',
      },
      {
        value: 'total_checked_stores',
        label: 'Total manually checked',
        description: 'Stores with a valid check (Available, Unavailable, Out of stock, Available online only, or Price updated within expiry window).',
        family: 'check_scope',
        countField: 'total_checked_stores',
      },
      {
        value: 'out_of_stock_checked',
        label: 'Checked — Out of stock',
        description: 'Manually checked and marked Out of stock (valid within 14 days).',
        family: 'check_oos',
        countField: 'out_of_stock_checked',
      },
      {
        value: 'unavailable_checked',
        label: 'Checked — Not available',
        description: 'Manually checked and marked Unavailable (valid within 84 days).',
        family: 'check_unavailable',
        countField: 'unavailable_checked',
      },
      {
        value: 'available_checked',
        label: 'Checked — Available',
        description: 'Manually checked and marked Available (valid within 30 days).',
        family: 'check_available',
        countField: 'available_checked',
      },
      {
        value: 'online_only_checked',
        label: 'Checked — Available online only',
        description: 'Manually checked and marked Available online only (valid within 30 days).',
        family: 'check_online_only',
        countField: 'online_only_checked',
      },
      {
        value: 'price_updated_checked',
        label: 'Checked — Price updated',
        description: 'Auto-resolved because the store price was refreshed after a stale-price report (valid within 30 days).',
        family: 'check_price_updated',
        countField: 'price_updated_checked',
      },
    ],
  },
  {
    group: 'percent_total',
    groupLabel: '% of total stores',
    options: [
      {
        value: 'not_updated_pct',
        label: 'Price not updated %',
        description: 'Share of all stores with price older than 14 days.',
        family: 'price_bad',
        countField: 'not_updated_stores',
        denominator: 'total_stores',
      },
      {
        value: 'updated_pct',
        label: 'Price updated %',
        description: 'Share of all stores with price within 14 days.',
        family: 'price_good',
        countField: 'updated_stores',
        denominator: 'total_stores',
      },
      {
        value: 'checked_coverage_pct',
        label: 'Manually checked %',
        description: 'Share of all stores that have a valid manual check.',
        family: 'check_scope',
        countField: 'total_checked_stores',
        denominator: 'total_stores',
      },
      {
        value: 'out_of_stock_pct',
        label: 'Out of stock % (of total)',
        description: 'Share of all stores marked Out of stock.',
        family: 'check_oos',
        countField: 'out_of_stock_checked',
        denominator: 'total_stores',
      },
      {
        value: 'unavailable_pct',
        label: 'Not available % (of total)',
        description: 'Share of all stores marked Unavailable.',
        family: 'check_unavailable',
        countField: 'unavailable_checked',
        denominator: 'total_stores',
      },
      {
        value: 'available_pct',
        label: 'Available % (of total)',
        description: 'Share of all stores marked Available.',
        family: 'check_available',
        countField: 'available_checked',
        denominator: 'total_stores',
      },
      {
        value: 'online_only_pct',
        label: 'Available online only % (of total)',
        description: 'Share of all stores marked Available online only.',
        family: 'check_online_only',
        countField: 'online_only_checked',
        denominator: 'total_stores',
      },
      {
        value: 'price_updated_pct',
        label: 'Price updated check % (of total)',
        description: 'Share of all stores auto-resolved as Price updated.',
        family: 'check_price_updated',
        countField: 'price_updated_checked',
        denominator: 'total_stores',
      },
    ],
  },
  {
    group: 'percent_checked',
    groupLabel: '% of manually checked',
    options: [
      {
        value: 'out_of_stock_pct_of_checked',
        label: 'Out of stock % (of checked)',
        description: 'Share of manually checked stores marked Out of stock.',
        family: 'check_oos',
        countField: 'out_of_stock_checked',
        denominator: 'total_checked_stores',
      },
      {
        value: 'unavailable_pct_of_checked',
        label: 'Not available % (of checked)',
        description: 'Share of manually checked stores marked Unavailable.',
        family: 'check_unavailable',
        countField: 'unavailable_checked',
        denominator: 'total_checked_stores',
      },
      {
        value: 'available_pct_of_checked',
        label: 'Available % (of checked)',
        description: 'Share of manually checked stores marked Available.',
        family: 'check_available',
        countField: 'available_checked',
        denominator: 'total_checked_stores',
      },
      {
        value: 'online_only_pct_of_checked',
        label: 'Available online only % (of checked)',
        description: 'Share of manually checked stores marked Available online only.',
        family: 'check_online_only',
        countField: 'online_only_checked',
        denominator: 'total_checked_stores',
      },
      {
        value: 'price_updated_pct_of_checked',
        label: 'Price updated % (of checked)',
        description: 'Share of checked stores auto-resolved as Price updated.',
        family: 'check_price_updated',
        countField: 'price_updated_checked',
        denominator: 'total_checked_stores',
      },
    ],
  },
];

export const METRIC_COLOR_THEME = {
  scope: { base: '#e3f2fd', strong: '#1565c0', textDark: '#1565c0', textLight: '#ffffff', accent: '#1565c0' },
  price_bad: { base: '#fce4ec', strong: '#e91e63', textDark: '#880e4f', textLight: '#ffffff', accent: '#e91e63' },
  price_good: { base: '#e8f5e9', strong: '#66bb6a', textDark: '#1b5e20', textLight: '#ffffff', accent: '#43a047' },
  check_scope: { base: '#ede7f6', strong: '#7e57c2', textDark: '#4527a0', textLight: '#ffffff', accent: '#7e57c2' },
  check_available: { base: '#ffebee', strong: '#f44336', textDark: '#b71c1c', textLight: '#ffffff', accent: '#f44336' },
  check_online_only: { base: '#e3f2fd', strong: '#2196F3', textDark: '#0d47a1', textLight: '#ffffff', accent: '#2196F3' },
  check_unavailable: { base: '#eeeeee', strong: '#787575', textDark: '#212121', textLight: '#ffffff', accent: '#787575' },
  check_oos: { base: '#fff3e0', strong: '#f3a72f', textDark: '#e65100', textLight: '#ffffff', accent: '#f3a72f' },
  check_price_updated: { base: '#e8f5e9', strong: '#43a047', textDark: '#1b5e20', textLight: '#ffffff', accent: '#43a047' },
};

const ALL_METRIC_OPTIONS = METRIC_GROUPS.flatMap((g) => g.options);

export function findMetricOption(metricKey) {
  return ALL_METRIC_OPTIONS.find((o) => o.value === metricKey) || ALL_METRIC_OPTIONS[1];
}

export function isPercentMetric(metricKey) {
  const opt = findMetricOption(metricKey);
  return Boolean(opt.denominator);
}

export function isPercentOfChecked(metricKey) {
  const opt = findMetricOption(metricKey);
  return opt.denominator === 'total_checked_stores';
}

function hexToRgb(hex) {
  const h = hex.replace('#', '');
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
}

function blendHex(lightHex, strongHex, ratio) {
  const t = Math.max(0, Math.min(1, ratio));
  const a = hexToRgb(lightHex);
  const b = hexToRgb(strongHex);
  const r = Math.round(a.r + (b.r - a.r) * t);
  const g = Math.round(a.g + (b.g - a.g) * t);
  const bl = Math.round(a.b + (b.b - a.b) * t);
  return `rgb(${r}, ${g}, ${bl})`;
}

export function getMetricRawValue(stats, metricKey) {
  if (!stats || !stats.total_stores) return null;
  const opt = findMetricOption(metricKey);
  if (!opt.denominator) {
    return Number(stats[opt.countField]) || 0;
  }
  const denom = stats[opt.denominator] || 0;
  if (denom === 0) return null;
  const num = Number(stats[opt.countField]) || 0;
  return Math.round((num / denom) * 100);
}

export function getMetricDisplayValue(stats, metricKey) {
  const raw = getMetricRawValue(stats, metricKey);
  if (raw === null) return '-';
  if (isPercentMetric(metricKey)) return `${raw}%`;
  return String(raw);
}

function getIntensityRatio(stats, metricKey, columnMax) {
  if (!stats || !stats.total_stores) return 0;
  const opt = findMetricOption(metricKey);

  if (opt.denominator) {
    const pct = getMetricRawValue(stats, metricKey);
    if (pct === null) return 0;
    if (opt.family === 'price_bad' && pct >= 70) return 1;
    return pct / 100;
  }

  const value = Number(stats[opt.countField]) || 0;
  if (metricKey === 'total_stores') {
    if (!columnMax) return value > 0 ? 0.3 : 0;
    return Math.min(1, value / columnMax);
  }

  const total = stats.total_stores || 1;
  const ratio = value / total;
  if (opt.family === 'price_bad' && ratio >= 0.7) return 1;
  return Math.min(1, ratio);
}

export function getMetricCellStyle(stats, metricKey, columnMax) {
  if (!stats || !stats.total_stores) {
    return { backgroundColor: '#ffffff', color: 'inherit', borderLeft: undefined };
  }

  const opt = findMetricOption(metricKey);
  const theme = METRIC_COLOR_THEME[opt.family] || METRIC_COLOR_THEME.scope;
  let ratio = getIntensityRatio(stats, metricKey, columnMax);

  if (isPercentMetric(metricKey)) {
    ratio = ratio * 0.85;
  }

  const minRatio = 0.15;
  const effectiveRatio = Math.max(minRatio, ratio);
  const backgroundColor = blendHex(theme.base, theme.strong, effectiveRatio);
  const color = effectiveRatio >= 0.5 ? theme.textLight : theme.textDark;
  const borderLeft = isPercentOfChecked(metricKey)
    ? `3px solid ${theme.accent}`
    : undefined;

  return { backgroundColor, color, borderLeft };
}

export function getMetricTheme(metricKey) {
  const opt = findMetricOption(metricKey);
  return METRIC_COLOR_THEME[opt.family] || METRIC_COLOR_THEME.scope;
}

export function getMetricFooterValue(filteredRows, companyId, metricKey) {
  const opt = findMetricOption(metricKey);

  if (!opt.denominator) {
    let sum = 0;
    filteredRows.forEach((row) => {
      const cell = row.cells[companyId];
      if (cell) sum += Number(cell[opt.countField]) || 0;
    });
    return String(sum);
  }

  let sumNum = 0;
  let sumDenom = 0;
  filteredRows.forEach((row) => {
    const cell = row.cells[companyId];
    if (cell) {
      sumNum += Number(cell[opt.countField]) || 0;
      sumDenom += Number(cell[opt.denominator]) || 0;
    }
  });

  if (sumDenom === 0) return '-';
  return `${Math.round((sumNum / sumDenom) * 100)}%`;
}

export function computeColumnMax(filteredRows, companyId) {
  let max = 0;
  filteredRows.forEach((row) => {
    const cell = row.cells[companyId];
    if (cell && cell.total_stores > max) max = cell.total_stores;
  });
  return max;
}

export const DATE_PERIOD_OPTIONS = [
  { value: 14, label: '14 Days' },
  { value: 30, label: '1 Month' },
  { value: 90, label: '3 Months' },
  { value: 180, label: '6 Months' },
];

export const METRIC_PRESETS = [
  { id: 'price_health', label: 'Price health', metric: 'not_updated_stores' },
  { id: 'price_pct', label: 'Price health %', metric: 'not_updated_pct' },
  { id: 'check_coverage', label: 'Check coverage', metric: 'total_checked_stores' },
  { id: 'check_coverage_pct', label: 'Check coverage %', metric: 'checked_coverage_pct' },
  { id: 'oos', label: 'Out of stock', metric: 'out_of_stock_checked' },
];

const METRIC_TO_COUNT_FIELD = {
  not_updated_pct: 'not_updated_stores',
  updated_pct: 'updated_stores',
  checked_coverage_pct: 'total_checked_stores',
  out_of_stock_pct: 'out_of_stock_checked',
  unavailable_pct: 'unavailable_checked',
  available_pct: 'available_checked',
  online_only_pct: 'online_only_checked',
  price_updated_pct: 'price_updated_checked',
  out_of_stock_pct_of_checked: 'out_of_stock_checked',
  unavailable_pct_of_checked: 'unavailable_checked',
  available_pct_of_checked: 'available_checked',
  online_only_pct_of_checked: 'online_only_checked',
  price_updated_pct_of_checked: 'price_updated_checked',
};

export function getMetricChartParams(metricKey) {
  const opt = findMetricOption(metricKey);
  if (opt.denominator) {
    return { metric_field: opt.countField, is_percent: true };
  }
  return { metric_field: opt.countField, is_percent: false };
}

export function mapMetricToStoreStatusFilter(metricKey) {
  if (metricKey.includes('not_updated') || metricKey === 'not_updated_stores') return 'not_updated';
  // Avoid matching price_updated_checked / price_updated_pct* (OOD check status 5)
  if (metricKey.includes('price_updated')) return '';
  if (metricKey.includes('updated') && !metricKey.includes('not_updated')) return 'updated';
  return '';
}

export function buildStoreDrilldownUrl(userTypeId, projectId, pmgId, companyId, metricKey, datePeriod) {
  const params = new URLSearchParams({
    pmg_id: String(pmgId),
    company_id: String(companyId),
    metric: metricKey,
    datePeriod: String(datePeriod),
  });
  const status = mapMetricToStoreStatusFilter(metricKey);
  if (status) params.set('status', status);
  return `/company-report-tool-out-of-date-store/${userTypeId}/${projectId}?${params.toString()}`;
}

export function getStatsTooltipText(stats) {
  if (!stats || !stats.total_stores) {
    return 'No store data for this product and banner.';
  }
  const pct = (n, d) => (d ? Math.round((n / d) * 100) : 0);
  const t = stats.total_stores;
  return [
    `Total: ${t}`,
    `Not updated: ${stats.not_updated_stores} (${pct(stats.not_updated_stores, t)}%)`,
    `Updated: ${stats.updated_stores} (${pct(stats.updated_stores, t)}%)`,
    `Checked: ${stats.total_checked_stores} (${pct(stats.total_checked_stores, t)}%)`,
    `OOS: ${stats.out_of_stock_checked} | N/A: ${stats.unavailable_checked} | Avail: ${stats.available_checked} | Online: ${stats.online_only_checked || 0} | Price upd: ${stats.price_updated_checked || 0}`,
    stats.not_updated_delta != null ? `Δ not updated vs prev cache: ${stats.not_updated_delta >= 0 ? '+' : ''}${stats.not_updated_delta}` : null,
  ].filter(Boolean).join(' | ');
}

export function isProblemRow(row, thresholdPct = 50, minBanners = 1) {
  let count = 0;
  Object.values(row.cells || {}).forEach((cell) => {
    if (!cell || !cell.total_stores) return;
    const pct = Math.round((cell.not_updated_stores / cell.total_stores) * 100);
    if (pct >= thresholdPct) count += 1;
  });
  return count >= minBanners;
}

export function getHeatmapPercentile(stats, metricKey, allValues) {
  const raw = getMetricRawValue(stats, metricKey);
  if (raw === null || !allValues.length) return 0;
  const sorted = [...allValues].sort((a, b) => a - b);
  const idx = sorted.findIndex((v) => v >= raw);
  if (idx < 0) return 1;
  return idx / Math.max(1, sorted.length - 1);
}

export function getHeatmapCellStyle(stats, metricKey, percentile) {
  if (!stats || !stats.total_stores) {
    return { backgroundColor: '#ffffff', color: 'inherit', borderLeft: undefined };
  }
  const opt = findMetricOption(metricKey);
  const theme = METRIC_COLOR_THEME[opt.family] || METRIC_COLOR_THEME.scope;
  const ratio = Math.max(0.15, percentile);
  const backgroundColor = blendHex(theme.base, theme.strong, ratio);
  const color = ratio >= 0.5 ? theme.textLight : theme.textDark;
  const borderLeft = isPercentOfChecked(metricKey) ? `3px solid ${theme.accent}` : undefined;
  return { backgroundColor, color, borderLeft };
}

export function collectMetricValues(rows, companyId, metricKey) {
  const values = [];
  rows.forEach((row) => {
    const cell = row.cells[companyId];
    const raw = getMetricRawValue(cell, metricKey);
    if (raw !== null) values.push(raw);
  });
  return values;
}

export function sortRowsByColumn(rows, companyId, metricKey, direction = 'asc') {
  const mult = direction === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    const aVal = getMetricRawValue(a.cells[companyId], metricKey);
    const bVal = getMetricRawValue(b.cells[companyId], metricKey);
    if (aVal === null && bVal === null) return a.name.localeCompare(b.name);
    if (aVal === null) return 1;
    if (bVal === null) return -1;
    if (aVal !== bVal) return (aVal - bVal) * mult;
    return a.name.localeCompare(b.name);
  });
}

export function buildAllMetricsCsv(rows, columns) {
  const countMetrics = METRIC_GROUPS.find((g) => g.group === 'count').options;
  let csv = 'PMG ID,Product name,Banner,Metric,Value\n';
  rows.forEach((row) => {
    columns.forEach((col) => {
      const stats = row.cells[col.company_id];
      countMetrics.forEach((m) => {
        const val = getMetricDisplayValue(stats, m.value);
        csv += `${row.pmg_id},"${row.name.replace(/"/g, '""')}","${col.banner_name}","${m.label}",${val === '-' ? '' : val}\n`;
      });
      METRIC_GROUPS.filter((g) => g.group !== 'count').forEach((g) => {
        g.options.forEach((m) => {
          const val = getMetricDisplayValue(stats, m.value);
          csv += `${row.pmg_id},"${row.name.replace(/"/g, '""')}","${col.banner_name}","${m.label}",${val === '-' ? '' : val}\n`;
        });
      });
    });
  });
  return csv;
}

export function loadSavedMetric(projectId) {
  try {
    const v = localStorage.getItem(`ood_metric_${projectId}`);
    return v && findMetricOption(v).value === v ? v : null;
  } catch (e) {
    return null;
  }
}

export function saveMetricPreference(projectId, metricKey) {
  try {
    localStorage.setItem(`ood_metric_${projectId}`, metricKey);
  } catch (e) {
    // ignore
  }
}

export function pivotPmgCompanyData(data) {
  const products = {};
  const columnsMap = new Map();

  data.forEach((row) => {
    if (!products[row.pmg_id]) {
      products[row.pmg_id] = { name: row.name, pmg_id: row.pmg_id, cells: {} };
    }
    columnsMap.set(row.company_id, row.banner_name);
    products[row.pmg_id].cells[row.company_id] = { ...row };
  });

  const columns = Array.from(columnsMap.entries())
    .map(([company_id, banner_name]) => ({ company_id, banner_name }))
    .sort((a, b) => a.banner_name.localeCompare(b.banner_name));

  return {
    rows: Object.values(products).sort((a, b) => a.name.localeCompare(b.name)),
    columns,
  };
}
